// The program's names for the ids a contract-model row carries: which
// deliverable, which team, which period, which optional task and exercise.
// One hook so every screen and drawer reads "Box Admin & Cloud" for the same
// workstream id, and nothing ever renders a raw id.
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { authClient } from '../../../lib/connect/clients';
import { contractDateISO } from '../../../lib/contractDate';
import { optionalTaskHooks, popHooks, useOptionalTaskExercisesForProgram } from '../Programs/operationalHooks';
import { programContactHooks } from '../Programs/relationshipHooks';
import { useDuties, useWorkstreams } from './hooks';
export function useCmLookups(programId) {
    const duties = useDuties(programId, {
        includeRules: true
    });
    const workstreams = useWorkstreams(programId);
    const pops = popHooks.useList({
        programId
    });
    const ots = optionalTaskHooks.useList({
        programId
    });
    const exercises = useOptionalTaskExercisesForProgram(programId);
    const contacts = programContactHooks.useList({
        programId
    });
    return useMemo(()=>{
        const dutyList = duties.data?.duties ?? [];
        const ruleList = duties.data?.rules ?? [];
        const rulesByDuty = new Map();
        for (const r of ruleList){
            const arr = rulesByDuty.get(r.dutyId) ?? [];
            arr.push(r);
            rulesByDuty.set(r.dutyId, arr);
        }
        const checkCodes = new Map();
        for (const [k, v] of Object.entries(duties.data?.checkCodes ?? {}))checkCodes.set(k, v.codes);
        const wsList = workstreams.data ?? [];
        const wsById = new Map(wsList.map((w)=>[
                w.id,
                w
            ]));
        const periods = (pops.data?.items ?? []).map((p)=>({
                id: p.id,
                name: p.periodName || (p.optionNumber ? `Option ${p.optionNumber}` : 'Base'),
                start: contractDateISO(p.startDate),
                end: contractDateISO(p.endDate),
                sortOrder: p.sortOrder,
                exercised: p.exercised
            })).sort((a, b)=>(a.start || '').localeCompare(b.start || '') || a.sortOrder - b.sortOrder);
        const otList = (ots.data?.items ?? []).map((t)=>({
                id: t.id,
                name: t.name,
                number: t.taskNumber,
                short: t.taskNumber ? `OT${t.taskNumber}` : t.name
            }));
        const exList = (exercises.data ?? []).map((e)=>({
                id: e.id,
                optionalTaskId: e.optionalTaskId,
                clinNumber: e.clinNumber,
                start: contractDateISO(e.popStartDate),
                end: contractDateISO(e.popEndDate)
            }));
        const contactList = (contacts.data?.items ?? []).map((c)=>({
                id: c.contactId || c.id,
                name: c.contactName || c.contactEmail || 'Unnamed contact',
                email: c.contactEmail
            }));
        return {
            isLoading: duties.isLoading || workstreams.isLoading,
            duties: dutyList,
            dutyById: new Map(dutyList.map((d)=>[
                    d.id,
                    d
                ])),
            rules: ruleList,
            rulesByDuty,
            checkCodes,
            workstreams: wsList,
            workstreamById: wsById,
            periods,
            periodById: new Map(periods.map((p)=>[
                    p.id,
                    p
                ])),
            optionalTasks: otList,
            optionalTaskById: new Map(otList.map((t)=>[
                    t.id,
                    t
                ])),
            exercises: exList,
            exerciseById: new Map(exList.map((e)=>[
                    e.id,
                    e
                ])),
            contacts: contactList,
            teamName: (id)=>id ? wsById.get(id)?.name ?? 'Unknown team' : 'Contract-wide'
        };
    }, [
        duties.data,
        duties.isLoading,
        workstreams.data,
        workstreams.isLoading,
        pops.data,
        ots.data,
        exercises.data,
        contacts.data
    ]);
}
/**
 * Tenant users' names by id, for "Waiver proposed by Ana on 3 Mar". A
 * proposal carries only the user id; the sharing directory is readable by
 * every signed-in user (unlike the admin people list). The viewer reads as
 * "you".
 */ export function useUserNames() {
    const { user } = useAuth();
    const q = useQuery({
        queryKey: [
            'cm-user-names'
        ],
        staleTime: 5 * 60000,
        queryFn: async ()=>(await authClient.listUsersForSharing({
                query: ''
            })).users
    });
    return useCallback((userId)=>{
        if (!userId) return 'someone';
        if (user?.id && userId === user.id) return 'you';
        const u = q.data?.find((x)=>x.id === userId);
        return u?.name || u?.email || 'a teammate';
    }, [
        q.data,
        user?.id
    ]);
}
